/**
 * BenchmarkSummaryRow — IP-protected benchmark headline + deal-page CTA.
 *
 * HANDOFF_AI_CHAT_FEATURE_V1_REVISED visual-feedback addendum (S16).
 *
 * V1 surfaces percentile rank only; the full distribution stays on the deal
 * benchmark page so the chat panel doesn't substitute for the paid surface.
 */
import { ExternalLink } from 'react-feather';
import { Link } from 'react-router-dom';

import { FinancialNumber } from '../../../components/ui';
import type { ToolResultProps } from '../lib/component-registry';

interface BenchmarkResult {
  deal_id: number;
  benchmark_available?: boolean;
  reason?: string;
  cohort_size?: number;
  deal_percentile_ranks?: Record<string, number | null | undefined>;
  deal_values?: Record<string, number | null | undefined>;
}

const FIELD_LABELS: Record<string, string> = {
  margin: 'Margin',
  arrangement_fee: 'Arrangement fee',
  leverage: 'Leverage',
  interest_coverage: 'Interest coverage',
};

export function BenchmarkSummaryRow({ result }: ToolResultProps) {
  const r = (result ?? {}) as Partial<BenchmarkResult>;
  if (!r || !r.deal_id) {return null;}
  if (r.benchmark_available === false) {
    return (
      <div className="mt-2 border border-border bg-surface-subtle px-3 py-2 text-body-sm text-txt-secondary">
        Benchmarks unavailable for this cohort{r.reason ? ` (${r.reason.replace(/_/g, ' ')})` : ''}.
      </div>
    );
  }

  const ranks = r.deal_percentile_ranks ?? {};
  const values = r.deal_values ?? {};
  const fields = Object.keys(ranks);

  if (fields.length === 0) {return null;}

  return (
    <div className="mt-2 border border-border bg-surface-card px-3 py-2 text-body-sm">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-txt-primary">
          Market percentile{r.cohort_size ? ` (n=${r.cohort_size})` : ''}
        </p>
        <Link
          to={`/deals/${r.deal_id}/benchmarks`}
          className="hidden md:inline-flex items-center gap-1 text-orange hover:underline"
        >
          Open benchmarks
          <ExternalLink size={12} />
        </Link>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {fields.map((f) => {
          const rank = ranks[f];
          const value = values[f];
          return (
            <div key={f} className="flex flex-col">
              <span className="text-txt-tertiary">{FIELD_LABELS[f] ?? f}</span>
              <span className="font-semibold text-txt-primary">
                {rank != null ? `${Math.round(Number(rank))}th pct` : '—'}
                {value != null && (
                  <span className="ml-2 text-txt-secondary">
                    (<FinancialNumber value={Number(value)} format="number" />)
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
